import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Shield, Trophy, Play, Clock, MapPin, Loader2, Calendar, Heart
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatMatchTime(datetime) {
  if (!datetime) return '';
  const d = new Date(datetime);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return `Today ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  const tmrw = new Date(now); tmrw.setDate(tmrw.getDate() + 1);
  if (d.toDateString() === tmrw.toDateString()) return `Tomorrow ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function TeamProfile() {
  const { teamSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/teams/${teamSlug}`);
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [teamSlug]);

  // Check if user follows this team
  useEffect(() => {
    if (!token || !data?.team) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/user/following`, { headers, withCredentials: true })
      .then(res => {
        const teams = (res.data.teams || []).map(t => t.toLowerCase());
        setIsFollowing(teams.includes(data.team.name.toLowerCase()));
      })
      .catch(() => {});
  }, [token, data]);

  const toggleFollow = async () => {
    if (!token) { navigate('/login'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.post(`${API}/user/unfollow-team`, { team_name: data.team.name }, { headers, withCredentials: true });
      } else {
        await axios.post(`${API}/user/follow-team`, { team_name: data.team.name }, { headers, withCredentials: true });
        // Auto-follow the team's competitions
        for (const comp of data.competitions || []) {
          await axios.post(`${API}/user/follow-competition`, { competition_slug: comp.slug }, { headers, withCredentials: true }).catch(() => {});
        }
      }
      setIsFollowing(!isFollowing);
    } catch { /* silent */ }
    setFollowLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  if (!data?.team) {
    return (
      <div className="min-h-screen bg-[#0a0f1e]">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[80vh] text-white">
          <Shield className="w-16 h-16 text-gray-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Team Not Found</h2>
          <Button data-testid="team-back-btn" onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </div>
    );
  }

  const { team, competitions, matches, videos } = data;
  const liveMatches = matches.filter(m => m.is_live);
  const upcomingMatches = matches.filter(m => !m.is_live && m.start_time && new Date(m.start_time) > new Date());
  const pastMatches = matches.filter(m => !m.is_live && (!m.start_time || new Date(m.start_time) <= new Date()));

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/10 to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <button data-testid="team-profile-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-6">
            <img
              src={team.crest_url}
              alt={team.name}
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover bg-white/10 ring-2 ring-white/10 shadow-xl"
              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(team.name)}&background=0d1a12&color=10b981&size=128&bold=true`; }}
            />
            <div>
              <h1 data-testid="team-name" className="text-3xl sm:text-4xl font-black text-white tracking-tight">{team.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {team.code && <span className="text-emerald-400 text-sm font-bold">{team.code}</span>}
                {team.country && (
                  <span className="inline-flex items-center gap-1.5 text-gray-400 text-sm">
                    <MapPin className="w-3.5 h-3.5" /> {team.country}
                  </span>
                )}
                {team.group && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs font-semibold rounded-full border border-emerald-500/20">
                    Group {team.group}
                  </span>
                )}
                {team.confederation && (
                  <span className="text-gray-500 text-xs">{team.confederation}</span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Competitions + Follow Button */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              data-testid="follow-team-btn"
              onClick={toggleFollow}
              disabled={followLoading}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                isFollowing
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
              }`}
            >
              <Heart className={`w-4 h-4 ${isFollowing ? 'fill-current' : ''}`} />
              {followLoading ? 'Updating...' : isFollowing ? 'Following' : 'Follow Team'}
            </Button>
            {competitions.map(comp => (
              <button
                key={comp.slug}
                data-testid={`comp-link-${comp.slug}`}
                onClick={() => navigate(`/competitions/${comp.slug}`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white text-xs font-medium rounded-full border border-white/10 transition-colors"
              >
                <Trophy className="w-3 h-3 text-emerald-400" /> {comp.short_name}
              </button>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-10">

        {/* Live Matches */}
        {liveMatches.length > 0 && (
          <section data-testid="team-live-matches">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" /></span>
              Live Now
            </h2>
            <div className="grid gap-4">
              {liveMatches.map(m => (
                <MatchRow key={m.id} match={m} onWatch={() => navigate(`/match/${m.id}`)} />
              ))}
            </div>
          </section>
        )}

        {/* Upcoming Matches */}
        {upcomingMatches.length > 0 && (
          <section data-testid="team-upcoming-matches">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" /> Upcoming
            </h2>
            <div className="grid gap-3">
              {upcomingMatches.map(m => (
                <MatchRow key={m.id} match={m} onWatch={() => navigate(`/watch/${m.id}`)} />
              ))}
            </div>
          </section>
        )}

        {/* Past Matches */}
        {pastMatches.length > 0 && (
          <section data-testid="team-past-matches">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" /> Results
            </h2>
            <div className="grid gap-3">
              {pastMatches.map(m => (
                <MatchRow key={m.id} match={m} onWatch={() => navigate(`/watch/${m.id}`)} />
              ))}
            </div>
          </section>
        )}

        {/* Related Videos */}
        {videos.length > 0 && (
          <section data-testid="team-videos">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-400" /> Videos
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map(v => (
                <div key={v.id} data-testid={`team-video-${v.id}`} onClick={() => navigate(`/watch/${v.id}`)} className="group cursor-pointer">
                  <div className="relative aspect-video rounded-xl overflow-hidden mb-2">
                    <img src={v.thumbnail_url || v.thumbnail || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600'} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-emerald-600 transition-all">
                        <Play className="w-4 h-4 text-white fill-current ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-white text-sm font-semibold group-hover:text-emerald-400 transition-colors line-clamp-2">{v.title}</h3>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {matches.length === 0 && videos.length === 0 && (
          <div className="text-center py-16">
            <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No matches or videos found for this team yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchRow({ match, onWatch }) {
  const home = match.home_team || {};
  const away = match.away_team || {};
  const teamsParts = (match.teams || '').split(' vs ');

  return (
    <div
      data-testid={`team-match-${match.id}`}
      onClick={onWatch}
      className="flex items-center gap-4 p-4 bg-[#1a2236] rounded-xl border border-white/5 hover:border-emerald-500/20 hover:bg-[#1e2844] transition-all cursor-pointer"
    >
      {match.is_live && (
        <span className="flex items-center gap-1.5 px-2 py-0.5 bg-red-600 rounded text-white text-[10px] font-bold uppercase shrink-0">
          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" /></span>
          LIVE
        </span>
      )}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <img src={home.crest_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(teamsParts[0] || 'H')}&size=64&background=0d1a12&color=10b981`} alt="" className="w-8 h-8 rounded-full object-cover bg-white/10" />
        <span className="text-white text-sm font-semibold truncate">{home.name || teamsParts[0]}</span>
      </div>
      <div className="text-center px-3 shrink-0">
        {match.is_live ? (
          <span className="text-lg font-black text-white tabular-nums">{match.home_score ?? 0} : {match.away_score ?? 0}</span>
        ) : match.start_time ? (
          <span className="text-xs text-gray-400">{formatMatchTime(match.start_time)}</span>
        ) : (
          <span className="text-sm font-bold text-white/30">VS</span>
        )}
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
        <span className="text-white text-sm font-semibold truncate text-right">{away.name || teamsParts[1]}</span>
        <img src={away.crest_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(teamsParts[1] || 'A')}&size=64&background=0d1a12&color=10b981`} alt="" className="w-8 h-8 rounded-full object-cover bg-white/10" />
      </div>
      {match.stage && (
        <span className="text-[10px] text-emerald-400/60 uppercase tracking-wider hidden sm:block shrink-0">{match.stage}</span>
      )}
    </div>
  );
}
